/**
 * Phaser 4 GameConfig — painterly isometric settings.
 *
 * §1.5 of the design spec defines the rendering targets. All choices are
 * documented inline so they can be revisited if the art style or target
 * platform changes.
 */

import * as Phaser from 'phaser';
import { BootScene } from '../scenes/BootScene';
import { PreloaderScene } from '../scenes/PreloaderScene';
import { TitleScene } from '../scenes/TitleScene';
import { CharacterCreationScene } from '../scenes/CharacterCreationScene';
import { ThresholdScene } from '../scenes/ThresholdScene';
import { SleepOverlayScene } from '../scenes/SleepOverlayScene';
import { FaintOverlayScene } from '../scenes/FaintOverlayScene';
import { JournalScene } from '../scenes/JournalScene';
import { ReliquaryScene } from '../scenes/ReliquaryScene';
import { UIScene } from '../scenes/UIScene';
import { WorldGalleryScene } from '../scenes/WorldGalleryScene';
import { AmbientOverlayScene } from '../scenes/painted/AmbientOverlayScene';
import { ForestHollowScene } from '../scenes/painted/ForestHollowScene';
import { HearthScene } from '../scenes/painted/HearthScene';
import { HilltopVistaScene } from '../scenes/painted/HilltopVistaScene';
import { LakesideStonesScene } from '../scenes/painted/LakesideStonesScene';
import { PathHomeScene } from '../scenes/painted/PathHomeScene';
import { CottageCrestScene } from '../scenes/painted/hill/CottageCrestScene';
import { CottageCrestGoldenScene } from '../scenes/painted/hill/CottageCrestGoldenScene';
import { TheCrossingScene } from '../scenes/painted/hill/TheCrossingScene';
import { HillPathWestScene } from '../scenes/painted/hill/HillPathWestScene';
import { HillPathMidScene } from '../scenes/painted/hill/HillPathMidScene';
import { HillPathEastScene } from '../scenes/painted/hill/HillPathEastScene';
import { WoodsEdgeScene } from '../scenes/painted/verdana/WoodsEdgeScene';
import { TalkingStumpScene } from '../scenes/painted/hill/TalkingStumpScene';
import { FarPillarScene } from '../scenes/painted/hill/FarPillarScene';
import { CottageInteriorScene } from '../scenes/painted/hill/CottageInteriorScene';
import { TheMeadowScene } from '../scenes/painted/hill/TheMeadowScene';
import { TheSlopeScene } from '../scenes/painted/hill/TheSlopeScene';
import { TheLowWallScene } from '../scenes/painted/hill/TheLowWallScene';
// Verdana area scenes (AGE-1466)
import { DonchitosHamletScene } from '../scenes/painted/verdana/DonchitosHamletScene';
import { DonchitosHamletRestoredScene } from '../scenes/painted/verdana/DonchitosHamletRestoredScene';
import { SaplingFieldsScene } from '../scenes/painted/verdana/SaplingFieldsScene';
import { WeepingStandingStoneScene } from '../scenes/painted/verdana/WeepingStandingStoneScene';
import { OldGroveShrineScene } from '../scenes/painted/verdana/OldGroveShrineScene';
import { SageRoadScene } from '../scenes/painted/verdana/SageRoadScene';
import { HamletInteriorScene } from '../scenes/painted/verdana/HamletInteriorScene';
import { RiversideWanderScene } from '../scenes/painted/verdana/RiversideWanderScene';
import { EmptyFieldScene } from '../scenes/painted/verdana/EmptyFieldScene';
import { RidgeAboveDonchitosScene } from '../scenes/painted/verdana/RidgeAboveDonchitosScene';
import { FallenLogScene } from '../scenes/painted/verdana/FallenLogScene';
// Sablewind area scenes (AGE-1466)
import { CaravanseraiScene } from '../scenes/painted/sablewind/CaravanseraiScene';
import { GrownOverOasisScene } from '../scenes/painted/sablewind/GrownOverOasisScene';
import { KiteHillShrineScene } from '../scenes/painted/sablewind/KiteHillShrineScene';
import { WindChimeHollowScene } from '../scenes/painted/sablewind/WindChimeHollowScene';
import { CaravanRoadScene } from '../scenes/painted/sablewind/CaravanRoadScene';
import { CaravanseraiInteriorScene } from '../scenes/painted/sablewind/CaravanseraiInteriorScene';
import { DuneCrestWanderScene } from '../scenes/painted/sablewind/DuneCrestWanderScene';
import { DryCanyonScene } from '../scenes/painted/sablewind/DryCanyonScene';
import { SandFlatScene } from '../scenes/painted/sablewind/SandFlatScene';
import { AbandonedLoomScene } from '../scenes/painted/sablewind/AbandonedLoomScene';
import { OpenDesertScene } from '../scenes/painted/sablewind/OpenDesertScene';
// Ember Mire area scenes (AGE-1465)
import { AshveinMarketScene } from '../scenes/painted/ember-mire/AshveinMarketScene';
import { JuggernautsHearthScene } from '../scenes/painted/ember-mire/JuggernautsHearthScene';
import { EmberShrineScene } from '../scenes/painted/ember-mire/EmberShrineScene';
import { CoolingFlowScene } from '../scenes/painted/ember-mire/CoolingFlowScene';
import { AshPassNightScene } from '../scenes/painted/ember-mire/AshPassNightScene';
import { SeedlingClearingScene } from '../scenes/painted/ember-mire/SeedlingClearingScene';
import { AshPassScene } from '../scenes/painted/ember-mire/AshPassScene';
import { AshveinForgeInteriorScene } from '../scenes/painted/ember-mire/AshveinForgeInteriorScene';
import { LavaTubeScene } from '../scenes/painted/ember-mire/LavaTubeScene';
import { CoolingCrustScene } from '../scenes/painted/ember-mire/CoolingCrustScene';
import { HollowLogBridgeScene } from '../scenes/painted/ember-mire/HollowLogBridgeScene';
import { CharGardenScene } from '../scenes/painted/ember-mire/CharGardenScene';
// Frostmere area scenes (AGE-1466)
import { FrostBridgeScene } from '../scenes/painted/frostmere/FrostBridgeScene';
import { FrostBridgeAfternoonScene } from '../scenes/painted/frostmere/FrostBridgeAfternoonScene';
import { HearthmereHoldScene } from '../scenes/painted/frostmere/HearthmereHoldScene';
import { WakeNotYetCairnScene } from '../scenes/painted/frostmere/WakeNotYetCairnScene';
import { FrozenFallsScene } from '../scenes/painted/frostmere/FrozenFallsScene';
import { NwPeaksScene } from '../scenes/painted/frostmere/NwPeaksScene';
import { ThawLineScene } from '../scenes/painted/frostmere/ThawLineScene';
import { FrostBridgeApproachScene } from '../scenes/painted/frostmere/FrostBridgeApproachScene';
import { HearthHallInteriorScene } from '../scenes/painted/frostmere/HearthHallInteriorScene';
import { IceCaveWanderScene } from '../scenes/painted/frostmere/IceCaveWanderScene';
import { SnowFieldScene } from '../scenes/painted/frostmere/SnowFieldScene';
import { FrozenLakeScene } from '../scenes/painted/frostmere/FrozenLakeScene';
import { TreelineScene } from '../scenes/painted/frostmere/TreelineScene';
import { PaintedSceneTransitionOverlay } from '../scenes/painted/SceneTransition';
import { PaintedOverworldShell } from '../scenes/painted/PaintedOverworldShell';
import { PaintedWorldCoordinator } from '../scenes/PaintedWorldCoordinator';
import { VideoTransitionScene } from '../scenes/painted/VideoTransitionScene';
import { TransitionFrameworkTestScene } from '../scenes/painted/TransitionFrameworkTestScene';
import { HomeScene } from '../scenes/HomeScene';
import { SceneSelectorScene } from '../scenes/SceneSelectorScene';
import { ShowcaseScene } from '../scenes/ShowcaseScene';
import { BuildToolsScene } from '../devtools/BuildToolsScene';
import { EditorScene } from '../devtools/EditorScene';

// ── Reference resolution ─────────────────────────────────────────────────────
// 960×540 is a 16:9 base that scales cleanly to 1920×1080 (×2) and 1280×720.
// All game-world coordinates are authored at this resolution.
export const REFERENCE_WIDTH = 960;
export const REFERENCE_HEIGHT = 540;

// ── Phaser GameConfig ────────────────────────────────────────────────────────
export const gameConfig: Phaser.Types.Core.GameConfig = {
  // ── Renderer ──────────────────────────────────────────────────────────────
  type: Phaser.AUTO, // WebGL when available, Canvas fallback

  // ── Canvas dimensions ─────────────────────────────────────────────────────
  width: REFERENCE_WIDTH,
  height: REFERENCE_HEIGHT,

  // ── Anti-aliasing / texture quality ───────────────────────────────────────
  // Painterly art uses smooth blending, NOT the nearest-neighbour filter that
  // pixelArt mode would force. Keep antialias on at all times.
  pixelArt: false,            // painterly: no nearest-neighbour forced mode
  antialias: true,            // smooth scaled / rotated textures in WebGL
  antialiasGL: true,          // also smooth at the WebGL context level
  roundPixels: false,         // no integer snapping — avoids sub-pixel wobble
                              //   in v4 the default is false; stated explicitly
                              //   for clarity.
  // HIGH-QUALITY mip-maps for zoom-out clarity. LINEAR_MIPMAP_LINEAR (a.k.a.
  // trilinear filtering) picks the two nearest mip levels and lerps between
  // them, giving the cleanest result when the isometric camera zooms out.
  mipmapFilter: 'LINEAR_MIPMAP_LINEAR',

  // ── Frame rate ─────────────────────────────────────────────────────────────
  // Cap the game loop at 60 fps regardless of monitor refresh rate to reduce
  // CPU/GPU load on high-refresh displays (120/144/240 Hz).
  fps: { target: 60, min: 20 },

  // ── Background ────────────────────────────────────────────────────────────
  backgroundColor: '#1a1a2e', // deep navy — matches dungeon ambient lighting

  // ── Input ─────────────────────────────────────────────────────────────────
  // Prevent the browser's context-menu from appearing on right-click so the
  // game can freely use right-mouse-button for secondary actions / camera pan.
  disableContextMenu: true,
  input: {
    touch: true,
  },

  // ── Banner (browser console) ───────────────────────────────────────────────
  // hidePhaser suppresses the "Phaser vX.Y.Z …" coloured banner from the
  // console. Keep the build output clean for players and QA.
  banner: {
    hidePhaser: true,
  },

  // ── Scenes ────────────────────────────────────────────────────────────────
  // Entry chain: Boot → Preloader → Title → CharacterCreation → Threshold → PaintedWorldCoordinator.
  // The first scene in this array is the only one Phaser auto-starts, so
  // BootScene must lead. CharacterCreationScene sits between Title and
  // Threshold; Title routes to it on "Enter the world" (B-009) and on Wake
  // it starts ThresholdScene which launches PaintedWorldCoordinator in background.
  // ThresholdScene (AGE-1026) masks the coordinator load behind atmosphere.
  //
  // Scene array order = render order (last entry renders on top). Two
  // parallel scenes anchor the top of the stack:
  //   - AmbientOverlayScene (AGE-1431) sits just below UIScene. It runs
  //     above every gameplay / modal scene so its drift motes and grain
  //     are the texture-match anchor across scene swaps.
  //   - UIScene is registered LAST so Phaser does not auto-start it and
  //     so the HUD renders on top of every other layer including the
  //     ambient overlay. Launched in parallel by the active world scene
  //     via `UIScene.ensureLaunched(this)`. See
  //     docs/design/scene-refactor-plan.md.
  scene: [
    BootScene,
    PreloaderScene,
    TitleScene,
    CharacterCreationScene,
    ThresholdScene,
    HomeScene,
    SleepOverlayScene,
    FaintOverlayScene,
    // JournalScene is the primary "menu" per GDD §14.3 — opened by M-key
    // (B-010). Registered after the entry chain so Phaser does not
    // auto-start it.
    JournalScene,
    // ReliquaryScene — server-wide shared Relic view (round 20 Phase F).
    ReliquaryScene,
    // WorldGalleryScene — dev contact-sheet viewer for the painted-world
    // wave-1 hero renders (AGE-1417). Reached via ?scene=worldGallery.
    // Registered after gameplay so Phaser does not auto-start it; BootScene
    // routes when the dev flag is present.
    WorldGalleryScene,
    // HearthScene — placeholder concrete `PaintedScene` (AGE-1419).
    // Proves the painted-scene framework with an inline asset spec; not
    // auto-started. Reached via `scene.start('HearthScene')` from a
    // PaintedScene edge zone or dev tooling.
    HearthScene,
    PathHomeScene,
    ForestHollowScene,
    LakesideStonesScene,
    HilltopVistaScene,
    // Hill area scenes (AGE-1462) — 9 painted scenes for The Hill zone.
    CottageCrestScene,
    CottageCrestGoldenScene,
    TheCrossingScene,
    HillPathWestScene,
    HillPathMidScene,
    HillPathEastScene,
    WoodsEdgeScene,
    TalkingStumpScene,
    FarPillarScene,
    CottageInteriorScene,
    TheMeadowScene,
    TheSlopeScene,
    TheLowWallScene,
    // Verdana area scenes (AGE-1466)
    DonchitosHamletScene,
    DonchitosHamletRestoredScene,
    SaplingFieldsScene,
    WeepingStandingStoneScene,
    OldGroveShrineScene,
    SageRoadScene,
    HamletInteriorScene,
    RiversideWanderScene,
    EmptyFieldScene,
    RidgeAboveDonchitosScene,
    FallenLogScene,
    // Sablewind area scenes (AGE-1466)
    CaravanseraiScene,
    GrownOverOasisScene,
    KiteHillShrineScene,
    WindChimeHollowScene,
    CaravanRoadScene,
    CaravanseraiInteriorScene,
    DuneCrestWanderScene,
    DryCanyonScene,
    SandFlatScene,
    AbandonedLoomScene,
    OpenDesertScene,
    // Ember Mire area scenes (AGE-1465)
    AshveinMarketScene,
    JuggernautsHearthScene,
    EmberShrineScene,
    CoolingFlowScene,
    AshPassNightScene,
    SeedlingClearingScene,
    AshPassScene,
    AshveinForgeInteriorScene,
    LavaTubeScene,
    CoolingCrustScene,
    HollowLogBridgeScene,
    CharGardenScene,
    // Frostmere area scenes (AGE-1466)
    FrostBridgeScene,
    FrostBridgeAfternoonScene,
    HearthmereHoldScene,
    WakeNotYetCairnScene,
    FrozenFallsScene,
    NwPeaksScene,
    ThawLineScene,
    FrostBridgeApproachScene,
    HearthHallInteriorScene,
    IceCaveWanderScene,
    SnowFieldScene,
    FrozenLakeScene,
    TreelineScene,
    // VideoTransitionScene — POC for AI-generated camera-movement video
    // transitions (AGE-1836). Launched on demand; never auto-started.
    VideoTransitionScene,
    // PaintedSceneTransitionOverlay — vignette layer driven by the
    // SceneTransition runner during doorway transitions (AGE-1421).
    // Launched on demand by the runner; never auto-started.
    PaintedSceneTransitionOverlay,
    // SMA-42 Phase 2 — PaintedOverworldShell controller scene that hosts
    // the painted-world overworld (Colyseus + SceneRouter + remote players).
    // Feature-flagged ON via `?paintedWorld=1` or env var; not auto-started.
    PaintedOverworldShell,
    // SMA-65 / SMA-68 — PaintedWorldCoordinator is the primary gameplay scene.
    // Owns the Colyseus connection, ZoneMessageBus, and coordinate normalization.
    // ThresholdScene launches it in the background during the cinematic load.
    PaintedWorldCoordinator,
    // TransitionFrameworkTestScene — dev harness for AGE-1421. Reached via
    // ?scene=transitionTest. BootScene routes when the dev flag is present.
    TransitionFrameworkTestScene,
    // ShowcaseScene (SMA-70) — Sprint 1 feature showcase room.
    // Reached via ?scene=showcase. All 10 Room 1 features in one scene.
    ShowcaseScene,
    // SceneSelectorScene (AGE-1437) — dev scene picker reached via the
    // "scenes" button on the title-screen options panel.
    SceneSelectorScene,
    // BuildToolsScene (SMA-142/143) — persistent dev overlay with tab bar
    // and scene browser sidebar. Reached via ?mode=build or auto in DEV mode.
    BuildToolsScene,
    // EditorScene (SMA-308) — dev-only room editor shell reached via
    // ?edit=<roomId>. Registered after build tools but BootScene routes to it
    // before the dev-mode build-tools default.
    EditorScene,
    // AmbientOverlayScene (AGE-1431) — persistent foreground overlay.
    // Registered second-to-last so it renders above every gameplay /
    // modal scene above. Launched on PreloaderScene completion; never
    // stopped or restarted thereafter.
    AmbientOverlayScene,
    // UIScene last — HUD renders on top of everything including the
    // ambient overlay. Phaser does not auto-start it; world scenes
    // launch it via `UIScene.ensureLaunched(this)`.
    UIScene,
  ],

  // ── Responsive scaling ────────────────────────────────────────────────────
  // FIT mode scales the canvas via CSS transform to fill the viewport while
  // maintaining the 16:9 aspect ratio, then centres it on both axes.
  //
  // Resize behaviour: Phaser's ScaleManager fires 'resize' events
  // automatically whenever the browser window changes size. Scenes read
  // this.scale.width / this.scale.height, which remain fixed at the
  // reference resolution (960×540) in FIT mode — no per-scene resize
  // handler is required for correct layout.
  //
  // Tested target resolutions (§1.7):
  //   1920×1080 — desktop full-HD  (canvas scaled ×2 exactly)
  //   1366×768  — common laptop    (canvas scaled ×1.42)
  //   2560×1440 — QHD / 1440p      (canvas scaled ×2.67)
  //   375×812   — mobile portrait  (canvas fits to 375px wide, ~210px tall)
  //   812×375   — mobile landscape (canvas fits to 667px wide, 375px tall)
  scale: {
    // Canvas parent. `#game-root` fills the viewport by default; when the C5
    // frame is mounted it becomes the console's plate slot (an exact 16:9 box),
    // so FIT lands pixel-flush inside the frame with no letterbox of its own.
    parent: 'game-root',
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    // Canonical game-world coordinate space — matches REFERENCE_WIDTH/HEIGHT
    // above. Placing them here makes ScaleManager the single source of truth
    // and avoids any ambiguity with the top-level width/height fields.
    width: REFERENCE_WIDTH,
    height: REFERENCE_HEIGHT,
    // Prevent the canvas from shrinking below a playable size on narrow
    // mobile-portrait viewports. At 320×180 the game is still functional
    // (though not designed for portrait play — mobile is deferred per spec).
    min: {
      width: 320,
      height: 180,
    },
  },
};
